import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  Segment,
  Button,
  Menu,
  Table,
  Dimmer,
  Loader,
  Dropdown,
  Input,
  Icon,
  Pagination,
  Modal,
  Form,
  Checkbox,
  Message,
  Image,
} from 'semantic-ui-react';
import {
  getBannerList,
  createBanner,
  modifyBanner,
  getBanner,
  deleteBanner,
  uploadBannerToTempFile,
} from '../api/BannerManagementApi';
import { BannerInfoDTO, BannerInputReqDTO } from '../dto/BannerManagementDTOs';
import { BannerType } from '../common/enums';

const BannerManagementPage: React.FC = () => {
  const authToken = localStorage.getItem('accessToken') || '';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTomorrow = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  };

  const getDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return formatDate(date);
  };

  const getToday = (): string => formatDate(new Date());

  const [loading, setLoading] = useState(true);
  const [bannerList, setBannerList] = useState<BannerInfoDTO[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [startDate, setStartDate] = useState(getDaysAgo(30));
  const [endDate, setEndDate] = useState(getTomorrow());
  const [selectedPeriod, setSelectedPeriod] = useState('30days');
  const [dateInputEnabled, setDateInputEnabled] = useState(false);
  const [searchTitle, setSearchTitle] = useState('');
  const [searchWriter, setSearchWriter] = useState('');
  const [filterBannerType, setFilterBannerType] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<string>('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingBannerId, setEditingBannerId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formBannerDesc, setFormBannerDesc] = useState('');
  const [formLink, setFormLink] = useState('');
  const [formBannerType, setFormBannerType] = useState<BannerType>(BannerType.MAIN);
  const [formActive, setFormActive] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [existingImgFileId, setExistingImgFileId] = useState('');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState<BannerInfoDTO | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imagePreviewTitle, setImagePreviewTitle] = useState('');

  const openImagePreview = (banner: BannerInfoDTO) => {
    if (!banner.imgUrl) return;
    setImagePreviewUrl(banner.imgUrl);
    setImagePreviewTitle(banner.title || '배너 이미지');
    setImagePreviewOpen(true);
  };

  const bannerTypeOptions = [
    { key: 'all', text: '전체', value: 'all' },
    { key: BannerType.MAIN, text: 'MAIN', value: BannerType.MAIN },
    { key: BannerType.SUB_1, text: 'SUB_1', value: BannerType.SUB_1 },
    { key: BannerType.SUB_2, text: 'SUB_2', value: BannerType.SUB_2 },
    { key: BannerType.SUB_3, text: 'SUB_3', value: BannerType.SUB_3 },
  ];

  const formBannerTypeOptions = [
    { key: BannerType.MAIN, text: 'MAIN', value: BannerType.MAIN },
    { key: BannerType.SUB_1, text: 'SUB_1', value: BannerType.SUB_1 },
    { key: BannerType.SUB_2, text: 'SUB_2', value: BannerType.SUB_2 },
    { key: BannerType.SUB_3, text: 'SUB_3', value: BannerType.SUB_3 },
  ];

  const activeOptions = [
    { key: 'all', text: '전체', value: 'all' },
    { key: 'true', text: '활성', value: 'true' },
    { key: 'false', text: '비활성', value: 'false' },
  ];

  const getBannerTypeLabel = (type: BannerType | null) => {
    if (!type) return '-';
    return type;
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    if (period === 'today') {
      setDateInputEnabled(false);
      setStartDate(getToday());
      setEndDate(getTomorrow());
    } else if (period === '7days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(7));
      setEndDate(getTomorrow());
    } else if (period === '30days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(30));
      setEndDate(getTomorrow());
    } else if (period === 'all') {
      setDateInputEnabled(true);
    }
  };

  const loadBannerList = async (page = currentPage) => {
    setLoading(true);
    try {
      const bannerType =
        filterBannerType === 'all' ? ('' as unknown as BannerType) : (filterBannerType as BannerType);
      const active =
        filterActive === 'all' ? null : filterActive === 'true';

      const result = await getBannerList(
        authToken,
        searchWriter.trim(),
        bannerType,
        searchTitle.trim(),
        active,
        new Date(startDate),
        new Date(endDate),
        page - 1, // API page는 0부터 시작
        pageSize
      );

      if (result && result.errorCode === 0) {
        setBannerList(result.response || []);
        setTotalPages(result.page?.last || 1);
      } else {
        setBannerList([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('배너 목록 로드 실패:', error);
      setBannerList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBannerList();
  }, [currentPage, startDate, endDate, filterBannerType, filterActive]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadBannerList(1);
  };

  const handlePageChange = (_e: React.MouseEvent, { activePage }: { activePage?: string | number }) => {
    setCurrentPage(Number(activePage) || 1);
  };

  const resetForm = () => {
    setFormTitle('');
    setFormBannerDesc('');
    setFormLink('');
    setFormBannerType(BannerType.MAIN);
    setFormActive(true);
    setSelectedFile(null);
    setPreviewUrl(null);
    setExistingImgFileId('');
    setFormError('');
    setEditingBannerId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openCreateModal = () => {
    resetForm();
    setModalMode('create');
    setModalOpen(true);
  };

  const openEditModal = async (banner: BannerInfoDTO) => {
    resetForm();
    setModalMode('edit');
    setEditingBannerId(banner.id);
    setModalOpen(true);
    setSubmitting(true);
    try {
      const result = await getBanner(authToken, banner.id);
      const detail = result?.errorCode === 0 && result.response ? result.response : banner;
      setFormTitle(detail.title || '');
      setFormBannerDesc(detail.bannerDesc || '');
      setFormLink(detail.link || '');
      setFormBannerType(detail.bannerType || BannerType.MAIN);
      setFormActive(detail.active);
      setExistingImgFileId(detail.imgFileId || '');
      if (detail.imgUrl) {
        setPreviewUrl(detail.imgUrl);
      }
    } catch (error) {
      console.error('배너 상세 조회 실패:', error);
      setFormTitle(banner.title || '');
      setFormBannerDesc(banner.bannerDesc || '');
      setFormLink(banner.link || '');
      setFormBannerType(banner.bannerType || BannerType.MAIN);
      setFormActive(banner.active);
      setExistingImgFileId(banner.imgFileId || '');
      if (banner.imgUrl) {
        setPreviewUrl(banner.imgUrl);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!formTitle.trim()) {
      setFormError('제목을 입력해주세요.');
      return;
    }
    if (modalMode === 'create' && !selectedFile) {
      setFormError('배너 이미지를 선택해주세요.');
      return;
    }

    setSubmitting(true);
    try {
      let imgTmpFileId: string | null = null;

      if (selectedFile) {
        const uploadResult = await uploadBannerToTempFile(authToken, selectedFile);
        if (!uploadResult || uploadResult.errorCode !== 0 || !uploadResult.response?.fileId) {
          setFormError('이미지 업로드에 실패했습니다.');
          setSubmitting(false);
          return;
        }
        imgTmpFileId = uploadResult.response.fileId;
      }

      const reqDto = new BannerInputReqDTO();
      reqDto.imgTmpFileId = imgTmpFileId;
      reqDto.bannerType = formBannerType;
      reqDto.title = formTitle.trim();
      reqDto.bannerDesc = formBannerDesc.trim();
      reqDto.link = formLink.trim();
      reqDto.active = formActive;

      let result;
      if (modalMode === 'create') {
        result = await createBanner(authToken, reqDto);
      } else if (editingBannerId != null) {
        result = await modifyBanner(authToken, editingBannerId, reqDto);
      }

      if (result && result.errorCode === 0) {
        setModalOpen(false);
        resetForm();
        loadBannerList();
      } else {
        setFormError(`배너 ${modalMode === 'create' ? '등록' : '수정'}에 실패했습니다.`);
      }
    } catch (error) {
      console.error('배너 저장 실패:', error);
      setFormError(`배너 ${modalMode === 'create' ? '등록' : '수정'} 중 오류가 발생했습니다.`);
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteConfirm = (banner: BannerInfoDTO) => {
    setDeletingBanner(banner);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingBanner) return;
    setDeleting(true);
    try {
      const result = await deleteBanner(authToken, deletingBanner.id);
      if (result && result.errorCode === 0) {
        setDeleteConfirmOpen(false);
        setDeletingBanner(null);
        loadBannerList();
      } else {
        alert('배너 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('배너 삭제 실패:', error);
      alert('배너 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container fluid>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Header as="h2" style={{ margin: 0 }}>
          9. 배너관리
        </Header>
        <Button primary icon labelPosition="left" onClick={openCreateModal}>
          <Icon name="plus" />
          배너 등록
        </Button>
      </div>

      <Segment
        raised
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          background: '#f8f9ff',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ marginRight: 8, fontWeight: 500 }}>기간</span>
          <Menu compact>
            <Menu.Item active={selectedPeriod === 'today'} onClick={() => handlePeriodChange('today')}>
              오늘
            </Menu.Item>
            <Menu.Item active={selectedPeriod === '7days'} onClick={() => handlePeriodChange('7days')}>
              7일
            </Menu.Item>
            <Menu.Item active={selectedPeriod === '30days'} onClick={() => handlePeriodChange('30days')}>
              30일
            </Menu.Item>
            <Menu.Item active={selectedPeriod === 'all'} onClick={() => handlePeriodChange('all')}>
              전체
            </Menu.Item>
          </Menu>
        </div>

        {dateInputEnabled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            <span>~</span>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 500 }}>유형:</span>
          <Dropdown
            selection
            options={bannerTypeOptions}
            value={filterBannerType}
            onChange={(_e, { value }) => {
              setCurrentPage(1);
              setFilterBannerType(value as string);
            }}
            style={{ minWidth: 120 }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 500 }}>상태:</span>
          <Dropdown
            selection
            options={activeOptions}
            value={filterActive}
            onChange={(_e, { value }) => {
              setCurrentPage(1);
              setFilterActive(value as string);
            }}
            style={{ minWidth: 100 }}
          />
        </div>

        <Input
          placeholder="작성자 닉네임/아이디"
          value={searchWriter}
          onChange={e => setSearchWriter(e.target.value)}
          onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') handleSearch();
          }}
          style={{ minWidth: 160 }}
        />

        <Input
          icon="search"
          iconPosition="left"
          placeholder="제목 검색"
          value={searchTitle}
          onChange={e => setSearchTitle(e.target.value)}
          onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') handleSearch();
          }}
          action={{ icon: 'search', onClick: handleSearch }}
          style={{ minWidth: 200 }}
        />
      </Segment>

      <Segment raised style={{ padding: '20px', marginBottom: 24, position: 'relative', minHeight: 200 }}>
        <Dimmer active={loading} inverted>
          <Loader>데이터를 불러오는 중...</Loader>
        </Dimmer>

        <Header as="h4" style={{ marginBottom: 16, marginTop: 0 }}>
          배너 목록
        </Header>

        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>No.</Table.HeaderCell>
              <Table.HeaderCell>이미지</Table.HeaderCell>
              <Table.HeaderCell>유형</Table.HeaderCell>
              <Table.HeaderCell>제목</Table.HeaderCell>
              <Table.HeaderCell>설명</Table.HeaderCell>
              <Table.HeaderCell>링크</Table.HeaderCell>
              <Table.HeaderCell>작성자</Table.HeaderCell>
              <Table.HeaderCell>상태</Table.HeaderCell>
              <Table.HeaderCell>관리</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {bannerList.length === 0 && !loading ? (
              <Table.Row>
                <Table.Cell colSpan={9} textAlign="center">
                  등록된 배너가 없습니다.
                </Table.Cell>
              </Table.Row>
            ) : (
              bannerList.map((banner, index) => (
                <Table.Row key={banner.id}>
                  <Table.Cell>{(currentPage - 1) * pageSize + index + 1}</Table.Cell>
                  <Table.Cell>
                    {banner.imgUrl ? (
                      <Image
                        src={banner.imgUrl}
                        size="tiny"
                        style={{ maxHeight: 48, width: 'auto', cursor: 'pointer' }}
                        onClick={() => openImagePreview(banner)}
                      />
                    ) : (
                      '-'
                    )}
                  </Table.Cell>
                  <Table.Cell>{getBannerTypeLabel(banner.bannerType)}</Table.Cell>
                  <Table.Cell>{banner.title}</Table.Cell>
                  <Table.Cell style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {banner.bannerDesc || '-'}
                  </Table.Cell>
                  <Table.Cell style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {banner.link || '-'}
                  </Table.Cell>
                  <Table.Cell>{banner.user?.name || banner.user?.loginid || '-'}</Table.Cell>
                  <Table.Cell>
                    <Button size="small" color={banner.active ? 'green' : 'grey'} style={{ minWidth: 60 }}>
                      {banner.active ? '활성' : '비활성'}
                    </Button>
                  </Table.Cell>
                  <Table.Cell>
                    <span
                      style={{ color: '#0066cc', cursor: 'pointer', marginRight: 12 }}
                      onClick={() => openEditModal(banner)}
                    >
                      수정
                    </span>
                    <span
                      style={{ color: '#cc0000', cursor: 'pointer' }}
                      onClick={() => openDeleteConfirm(banner)}
                    >
                      삭제
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>

        {totalPages > 1 && (
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              activePage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Segment>

      <Modal open={modalOpen} onClose={() => !submitting && setModalOpen(false)} size="small">
        <Modal.Header>{modalMode === 'create' ? '배너 등록' : '배너 수정'}</Modal.Header>
        <Modal.Content>
          {formError && (
            <Message negative>
              <Message.Header>{formError}</Message.Header>
            </Message>
          )}
          <Form loading={submitting}>
            <Form.Field required>
              <label>배너 유형</label>
              <Dropdown
                selection
                fluid
                options={formBannerTypeOptions}
                value={formBannerType}
                onChange={(_e, { value }) => setFormBannerType(value as BannerType)}
              />
            </Form.Field>
            <Form.Field required>
              <label>제목</label>
              <Input
                value={formTitle}
                onChange={e => setFormTitle(e.target.value)}
                placeholder="배너 제목"
              />
            </Form.Field>
            <Form.Field>
              <label>설명</label>
              <Form.TextArea
                value={formBannerDesc}
                onChange={(_e, { value }) => setFormBannerDesc(String(value || ''))}
                placeholder="배너 설명"
                rows={3}
              />
            </Form.Field>
            <Form.Field>
              <label>링크 URL</label>
              <Input
                value={formLink}
                onChange={e => setFormLink(e.target.value)}
                placeholder="https://"
              />
            </Form.Field>
            <Form.Field required={modalMode === 'create'}>
              <label>
                배너 이미지
                {modalMode === 'edit' && !selectedFile && (existingImgFileId || previewUrl) ? ' (변경 시에만 선택)' : ''}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
              {previewUrl && (
                <div style={{ marginTop: 12 }}>
                  <Image src={previewUrl} size="medium" bordered style={{ maxHeight: 180, width: 'auto' }} />
                </div>
              )}
            </Form.Field>
            <Form.Field>
              <Checkbox
                label="활성"
                checked={formActive}
                onChange={(_e, { checked }) => setFormActive(!!checked)}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)} disabled={submitting}>
            취소
          </Button>
          <Button primary onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {modalMode === 'create' ? '등록' : '수정'}
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)} size="tiny">
        <Modal.Header>배너 삭제</Modal.Header>
        <Modal.Content>
          <p>
            배너 &quot;{deletingBanner?.title}&quot; 을(를) 삭제하시겠습니까?
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            취소
          </Button>
          <Button negative onClick={handleDelete} loading={deleting} disabled={deleting}>
            삭제
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal open={imagePreviewOpen} onClose={() => setImagePreviewOpen(false)} size="large" closeIcon>
        <Modal.Header>{imagePreviewTitle}</Modal.Header>
        <Modal.Content style={{ textAlign: 'center' }}>
          {imagePreviewUrl && (
            <Image
              src={imagePreviewUrl}
              fluid
              style={{ maxHeight: '70vh', width: 'auto', margin: '0 auto' }}
            />
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setImagePreviewOpen(false)}>닫기</Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default BannerManagementPage;
